import { useState } from 'react';
import { HUMAN_PROMPT, SETUP_URL } from './agentPrompt';
import { ArchDiagram } from './ArchDiagram';
import { CHANNELS, glyphColorFor } from './channels';
import { DASHBOARD_UPGRADE_URL } from './config';
import { HeroVisual } from './HeroVisual';
import { Link } from './router';
import { TopNav } from './TopNav';

export function Home({ apiBase: _apiBase }: { apiBase: string }) {
  return (
    <div className="page">
      <div className="page-glow" aria-hidden="true" />
      <div className="container wide">
        <TopNav />

        <header className="hero">
          <div className="hero-copy">
            <h1>
              Give your AI a <span className="grad">voice</span>.
            </h1>
            <p className="tagline">
              Your AI texts you. You text back. On the apps you already
              use — replies, files, everything.
            </p>
            <PromptHero />
          </div>
          <div className="hero-art">
            <HeroVisual />
          </div>
        </header>

        <HowItWorks />

        <Bento />

        <Pricing />

        <CtaBand />

        <footer>
          <p className="footer-privacy">
            Built privacy-first. Messages auto-delete after 7 days — no ads,
            no tracking, no training on your messages.
          </p>
          <p>
            <a href="/privacy">privacy</a> · <a href="/terms">terms</a> ·{' '}
            <Link to="/setup">for agents</Link> ·{' '}
            <a href="/llms.txt">llms.txt</a>
          </p>
        </footer>
      </div>
    </div>
  );
}

function PromptHero() {
  return (
    <div className="prompt-hero" id="prompt">
      <p className="prompt-instr">Paste this into your AI chat:</p>
      <div className="prompt-card">
        <div className="prompt-text">
          Set up Vocal Cord from{' '}
          <Link to="/setup" className="prompt-link">
            {SETUP_URL}
          </Link>
        </div>
        <CopyButton text={HUMAN_PROMPT} />
      </div>
      <p className="prompt-foot">
        Your agent handles the rest — free, no signup form.{' '}
        <Link to="/setup">Preview what it reads →</Link>
      </p>
    </div>
  );
}

function CopyButton({ text, label = 'copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="copy-btn"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'copied!' : label}
    </button>
  );
}

/** The architecture, simplified: agent → MCP → AWS → providers → you,
 * and the reply path back. */
function HowItWorks() {
  return (
    <section id="how" className="how-it-works">
      <h2>How it works</h2>
      <p className="section-lede">
        Your agent calls one MCP tool. Our serverless stack does the rest —
        and your reply is pushed back to the agent the moment you send it.
      </p>
      <ArchDiagram />
    </section>
  );
}

/**
 * Bento grid: one wide conversation card, two square cards, and a wide
 * channels card with the live logo wall (keeps the /#channels anchor
 * that llms.txt points agents at).
 */
function Bento() {
  return (
    <section id="features">
      <h2>A real back-and-forth, not a notification firehose</h2>
      <div className="bento">
        <div className="bento-card bento-wide">
          <h3>Two-way, with memory</h3>
          <p>
            Reply from your phone and your AI picks up where it left off.
            <strong> Monitors</strong> keep it listening for your answer for
            up to 24 hours — no polling, no missed replies.
          </p>
          <div className="chat-demo" aria-hidden="true">
            <div className="chat-bubble ai">
              Deploy finished — 2 warnings in the logs. Want me to dig in?
              <span className="chat-att">📎 deploy-log.txt</span>
            </div>
            <div className="chat-bubble you">yes, and ping me if it's our code</div>
            <div className="chat-bubble ai">On it. I'll message you here when I know more.</div>
          </div>
        </div>

        <div className="bento-card">
          <h3>Files, both ways</h3>
          <p>
            Charts, screenshots, logs, PDFs. Your AI attaches files to any
            message — and reads the ones you send back.
          </p>
        </div>

        <div className="bento-card">
          <h3>Zero setup ceremony</h3>
          <p>
            One pasted prompt installs the MCP server and configures itself.
            Nothing to host, no SDK, no keys to rotate by hand.
          </p>
        </div>

        <div className="bento-card bento-wide" id="channels">
          <h3>On the apps you already use</h3>
          <p>One account reaches you everywhere — no new app to check.</p>
          <div className="channel-wall">
            {CHANNELS.filter((c) => c.available).map((c) => (
              <span className="channel-tile" key={c.kind}>
                <ChannelGlyph kind={c.kind} />
                {c.displayName}
              </span>
            ))}
            <span className="channel-tile more">more on the way</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChannelGlyph({ kind }: { kind: string }) {
  const d = CHANNELS.find((c) => c.kind === kind);
  if (!d) return null;
  const fill = glyphColorFor(d.brandColor);
  const tile =
    fill === 'var(--fg)'
      ? 'color-mix(in srgb, var(--fg) 12%, transparent)'
      : `color-mix(in srgb, ${d.brandColor} 22%, transparent)`;
  return (
    <span className="channel-glyph" style={{ background: tile }} aria-hidden="true">
      {d.logoPath ? (
        <svg width="16" height="16" viewBox="0 0 24 24">
          <path d={d.logoPath} fill={fill} />
        </svg>
      ) : (
        <span style={{ color: fill, fontWeight: 700, fontSize: '0.8em' }}>
          {d.letterMark ?? d.displayName[0]}
        </span>
      )}
    </span>
  );
}

function Pricing() {
  return (
    <section id="pricing">
      <h2>Two plans. No surprises.</h2>
      <div className="pricing">
        <div className="plan">
          <h3>Free</h3>
          <p className="plan-blurb">For hobby &amp; occasional personal use.</p>
          <div className="price">$0</div>
          <ul>
            <li>100 messages / month</li>
            <li>All channels</li>
          </ul>
          <Link to="/#prompt" className="cta secondary">
            Get started
          </Link>
        </div>
        <div className="plan paid">
          <span className="plan-tag">Covers our infra costs</span>
          <h3>Unlimited</h3>
          <p className="plan-blurb">For everyday and professional use.</p>
          <div className="price">
            $1.65 <span className="price-cycle">/ month</span>
          </div>
          <p className="price-note">billed annually at $19.80</p>
          <ul>
            <li>Unlimited messages<sup>*</sup></li>
            <li>All channels</li>
          </ul>
          <p className="fair-use">
            <sup>*</sup>Fair use: personal 1:1 between you and your AI,
            up to 10,000 messages / month.
          </p>
          <a href={DASHBOARD_UPGRADE_URL} className="cta primary">
            Upgrade
          </a>
        </div>
      </div>
    </section>
  );
}

/** Closing band — one last paste-the-prompt moment. */
function CtaBand() {
  return (
    <section className="cta-band">
      <h2>Give your AI a voice today.</h2>
      <p>Free to start. One paste. No signup form.</p>
      <div className="prompt-card">
        <div className="prompt-text">{HUMAN_PROMPT}</div>
        <CopyButton text={HUMAN_PROMPT} />
      </div>
    </section>
  );
}
